import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "../layout/PageHeader";
import "../Modal.css";
import {
  createAgencyJob,
  deleteAgencyJob,
  fetchAiHiringSettings,
  fetchAgencyJobs,
  fetchAgencyJobDistributionStatus,
  fetchAgencyPublicLinks,
  fetchAgencyBoardSettings,
  fetchLiveAvatarAgentOptions,
  invalidateIndeedFeedCache,
  updateAgencyJob,
  updateAiHiringSettings,
} from "../../utils/api";
// AIHiringPlanSelector (plain Member/Non-Member checkout) used to render
// here, duplicating the Member/Non-Member purchase flow now consolidated
// onto the Payment & Billing page (AIHiringFullBundleCard). Removed from
// this page only — the component file and its backend checkout route
// (POST /ai-hiring/create-checkout-session) are left intact for
// historical/in-flight sessions, they're just no longer rendered anywhere.

// Custom Questions are injected into the video-interview session prompt
// with mandatory framing (see buildMandatoryJobQuestionsSection in
// Albert-backend/services/videoRealtimeStream.js) — each question adds real
// token cost against OpenAI Realtime API's session.instructions limit, and
// that mandatory-framing budget assumes at most 5. This cap must be enforced
// here (not just server-side) so a recruiter never silently loses questions
// 6+ with no UI feedback.
const MAX_CUSTOM_QUESTIONS = 5;

// All AI Hiring locations use Evaluation Criteria checklist mode — no numeric
// score thresholds on this page (matches Unity - Princeton).
const USE_EVALUATION_CRITERIA = true;

const DEFAULT_INTERVIEW_APP_URL = "https://www.interview.myalbertai.com";

const STATIC_INTERVIEW_IMAGE_CATALOG = [
  { key: "kathy1", url: "/interviewers/Kathy1.jpg", name: "Kathy 1", azureVoiceGender: "female" },
  { key: "kathy2", url: "/interviewers/Kathy2.jpg", name: "Kathy 2", azureVoiceGender: "female" },
  { key: "bob1", url: "/interviewers/bob1.jpg", name: "Bob 1", azureVoiceGender: "male" },
  { key: "bob2", url: "/interviewers/bob2.jpg", name: "Bob 2", azureVoiceGender: "male" },
];

const buildStaticInterviewImageOptions = (interviewAppUrl) => {
  const base = String(interviewAppUrl || DEFAULT_INTERVIEW_APP_URL).replace(/\/+$/, "");
  return STATIC_INTERVIEW_IMAGE_CATALOG.map((img) => ({
    ...img,
    previewUrl: `${base}${img.url}`,
  }));
};

const sanitizePayInput = (value) => {
  const raw = String(value || "");
  let cleaned = raw.replace(/[^\d.]/g, "");
  const dotIdx = cleaned.indexOf(".");
  if (dotIdx >= 0) {
    cleaned =
      cleaned.slice(0, dotIdx + 1) + cleaned.slice(dotIdx + 1).replace(/\./g, "");
  }
  return cleaned;
};

const parsePayValue = (value) => {
  const parsed = Number(sanitizePayInput(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const AiHiringSettings = (props) => {
  const BOARD_PROVIDERS = [
    "indeed",
    "ziprecruiter",
    "monster",
    "linkedin",
    "glassdoor",
    "careerbuilder",
    "simplyhired",
    "snagajob",
    "care_com",
  ];
  const PROVIDER_LABELS = {
    indeed: "Indeed",
    ziprecruiter: "ZipRecruiter",
    monster: "Monster",
    linkedin: "LinkedIn",
    glassdoor: "Glassdoor",
    careerbuilder: "CareerBuilder",
    simplyhired: "SimplyHired",
    snagajob: "Snagajob",
    care_com: "Care.com",
  };
  const showTechnicalBoardConfig = false;
  const DEFAULT_REQUIREMENT_TAGS = [
    "nightshift",
    "day shift",
    "weekend availability",
    "dementia care",
    "hoyer lift",
    "cna",
    "driver's license",
    "bilingual",
  ];
  // Mirrors Albert-backend/src/config/evaluationCriteriaLibrary.js — the
  // "What matters for this role" checklist only rendered for agencies with
  // Agency.evaluationCriteriaEnabled (see Evaluation Criteria checklist
  // feature, replaces numeric score-threshold gating).
  const EVALUATION_CRITERIA_LIBRARY = [
    {
      criteriaId: "years_relevant_experience",
      label: "Years of relevant experience",
      category: "experience",
      source: "resume",
      defaultActive: true,
    },
    {
      criteriaId: "required_certification",
      label: "Required certification (CNA/HHA/etc)",
      category: "certification",
      source: "resume",
      defaultActive: true,
    },
    {
      criteriaId: "lives_within_distance",
      label: "Lives within acceptable distance",
      category: "logistics",
      source: "application",
      defaultActive: true,
    },
    {
      criteriaId: "reliable_transportation",
      label: "Has reliable transportation",
      category: "logistics",
      source: "any",
      defaultActive: true,
    },
    {
      criteriaId: "has_smartphone",
      label: "Has a smartphone/cell phone",
      category: "equipment",
      source: "application",
      defaultActive: true,
    },
    {
      criteriaId: "available_required_hours",
      label: "Available for required shift hours",
      category: "availability",
      source: "any",
      defaultActive: true,
    },
    {
      criteriaId: "comfortable_specialized_care",
      label: "Comfortable with specialized care type (e.g. dementia, hospice)",
      category: "experience",
      source: "any",
      defaultActive: false,
    },
    {
      criteriaId: "clear_background_check",
      label: "Clear background check",
      category: "logistics",
      source: "application",
      defaultActive: true,
    },
    {
      criteriaId: "valid_drivers_license",
      label: "Valid driver's license",
      category: "logistics",
      source: "application",
      defaultActive: false,
    },
    {
      criteriaId: "bilingual",
      label: "Bilingual (if required)",
      category: "custom",
      source: "any",
      defaultActive: false,
    },
  ];
  const EVALUATION_CRITERIA_CATEGORY_LABELS = {
    experience: "Experience",
    certification: "Certification",
    logistics: "Logistics",
    equipment: "Equipment",
    availability: "Availability",
    custom: "Custom",
  };
  const buildDefaultEvaluationCriteria = () =>
    EVALUATION_CRITERIA_LIBRARY.map((item) => ({
      criteriaId: item.criteriaId,
      label: item.label,
      category: item.category,
      source: item.source,
      isCustom: false,
      isActive: item.defaultActive !== false,
    }));
  const toInputDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };
  const todayInput = () => new Date().toISOString().slice(0, 10);
  const plusDaysInput = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + Number(days || 0));
    return d.toISOString().slice(0, 10);
  };
  // postedDate is stored as a UTC-anchored calendar day (see backend
  // routes/jobs.js's parsePostedDate: new Date(`${raw}T00:00:00.000Z`)), not
  // a specific moment — must format in UTC explicitly, or any timezone
  // behind UTC (e.g. US Eastern) shifts the displayed day back by one.
  const formatDisplayDate = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleDateString("en-US", { timeZone: "UTC" });
  };
  const formatDateTime = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleString("en-US");
  };
  const isExpiredByDate = (value) => {
    if (!value) return false;
    const expiry = new Date(value);
    if (Number.isNaN(expiry.getTime())) return false;
    const today = new Date();
    const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return expiry.getTime() < startToday.getTime();
  };
  const getJobLifecycleMeta = ({
    boardStatus,
    homecareStatus,
    indeedStatus,
    isExpired,
  }) => {
    if (isExpired || boardStatus === "expired") {
      return {
        label: "Closed",
        background: "rgba(107,114,128,.14)",
        color: "#374151",
        border: "1px solid rgba(107,114,128,.35)",
      };
    }
    if (boardStatus === "paused") {
      return {
        label: "Paused",
        background: "rgba(107,114,128,.14)",
        color: "#374151",
        border: "1px solid rgba(107,114,128,.35)",
      };
    }
    const applyLinkLive =
      homecareStatus === "posted" || boardStatus === "published";
    if (applyLinkLive) {
      if (indeedStatus === "posted") {
        return {
          label: "Live · Indeed",
          background: "rgba(16,185,129,.14)",
          color: "#047857",
          border: "1px solid rgba(16,185,129,.35)",
        };
      }
      if (indeedStatus === "failed") {
        return {
          label: "Apply Link Live",
          background: "rgba(16,185,129,.14)",
          color: "#047857",
          border: "1px solid rgba(16,185,129,.35)",
          hint: "Indeed review failed — copy link still works",
        };
      }
      return {
        label: "Apply Link Live",
        background: "rgba(16,185,129,.14)",
        color: "#047857",
        border: "1px solid rgba(16,185,129,.35)",
      };
    }
    if (indeedStatus === "posted") {
      return {
        label: "Indeed Live",
        background: "rgba(16,185,129,.14)",
        color: "#047857",
        border: "1px solid rgba(16,185,129,.35)",
      };
    }
    if (indeedStatus === "failed") {
      return {
        label: "Indeed Review Failed",
        background: "rgba(239,68,68,.12)",
        color: "#b91c1c",
        border: "1px solid rgba(239,68,68,.35)",
        hint: "Job may still be saved — use Resubmit Review or copy link below",
      };
    }
    if (indeedStatus === "queued" || indeedStatus === "pending") {
      return {
        label: "Pending Indeed Review",
        background: "rgba(245,158,11,.14)",
        color: "#b45309",
        border: "1px solid rgba(245,158,11,.35)",
      };
    }
    return {
      label: "Draft",
      background: "rgba(107,114,128,.14)",
      color: "#374151",
      border: "1px solid rgba(107,114,128,.35)",
    };
  };

  const locationId = props.activeLocation?.id || props.activeLocation?._id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resumeScore, setResumeScore] = useState(10);
  const [phoneScore, setPhoneScore] = useState(10);
  const [videoScore, setVideoScore] = useState(10);
  const [videoInterviewEnabled, setVideoInterviewEnabled] = useState(false);
  const [initialVideoInterviewEnabled, setInitialVideoInterviewEnabled] =
    useState(false);
  const [phoneInterviewEnabled, setPhoneInterviewEnabled] = useState(true);
  const [initialPhoneInterviewEnabled, setInitialPhoneInterviewEnabled] =
    useState(true);
  // Full raw resumeScreening/phoneInterview/videoInterview objects as last
  // fetched from the API (includes customPrompt, customPromptDraft,
  // knowledgeBaseDraft, customPromptMode, enabled, useDefaultQuestions,
  // customQuestions — none of which this page's UI edits anymore). Echoed
  // back unchanged on Save so this page can never clobber them, as
  // defense-in-depth alongside the field-level merge on the backend
  // (see routes/aiHiring.js PUT /settings).
  const [resumeScreeningRaw, setResumeScreeningRaw] = useState({});
  const [phoneInterviewRaw, setPhoneInterviewRaw] = useState({});
  const [videoInterviewRaw, setVideoInterviewRaw] = useState({});
  // Which conversation engine drives video AI interviews for this agency.
  // No longer editable from this page (2026-07-28) — Farzad now manages
  // this exclusively from interview.myalbertai.com/admin/video-ai-settings
  // (AiAlbertInterview's AdminVideoAiSettingsPage, which PATCHes the same
  // Agency.videoInterviewProvider field via
  // Albert-backend/routes/admin/agencies.js). Still fetched/kept here (not
  // read anywhere in this component currently, but round-tripped so a
  // future consumer — e.g. a provider-aware LiveAvatar Agent card — doesn't
  // need to re-add the fetch) and intentionally never sent back on Save
  // now that there's no UI here that can change it.
  const [videoInterviewProvider, setVideoInterviewProvider] =
    useState("openai_realtime");
  // Which LiveAvatar VISUAL avatar (avatar_id) this agency renders
  // (2026-07-28 — LiveAvatar Agent Selection). NOT gated on
  // videoInterviewProvider above and not admin-only — any agency user can
  // pick a look/voice regardless of provider. Mirrors
  // Agency.liveAvatarAgentSelection on the backend — see
  // utils/videoInterviewProvider.js's resolveLiveAvatarAgentId().
  const [liveAvatarAgentSelection, setLiveAvatarAgentSelection] =
    useState("agent_1");
  // Real name/preview_url per option, fetched from LiveAvatar's own Avatar
  // API (routes/liveavatar.js's GET /agent-options) — not agency-specific,
  // so this is fetched once on mount regardless of locationId.
  const [liveAvatarAgentOptions, setLiveAvatarAgentOptions] = useState({
    agent_1: { configured: true, name: "", previewUrl: "" },
    agent_2: { configured: false, name: "", previewUrl: "" },
  });
  const [staticInterviewImageUrl, setStaticInterviewImageUrl] = useState(
    "/interviewers/Kathy1.jpg",
  );
  const [staticInterviewImageOptions, setStaticInterviewImageOptions] = useState([]);
  const [interviewAppUrl, setInterviewAppUrl] = useState(DEFAULT_INTERVIEW_APP_URL);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState("");
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [jobSaving, setJobSaving] = useState(false);
  const [jobEditingId, setJobEditingId] = useState(null);
  const [jobBoardStatus, setJobBoardStatus] = useState({}); // { [jobId]: { indeed: status|null, ziprecruiter: status|null } }
  const [distributionStatusByJob, setDistributionStatusByJob] = useState({});
  const [distributionLoadingByJob, setDistributionLoadingByJob] = useState({});
  const [postingInProgress, setPostingInProgress] = useState({}); // { [jobId-provider]: true|false }
  const [bulkPosting, setBulkPosting] = useState({});
  const [bulkPostingCount, setBulkPostingCount] = useState({});
  const [postingError, setPostingError] = useState({});
  const [postToIndeed, setPostToIndeed] = useState(false);
  const [postToZipRecruiter, setPostToZipRecruiter] = useState(false);
  const [feedRefreshing, setFeedRefreshing] = useState(false);
  const [agencyId, setAgencyId] = useState("");
  const [agencySlug, setAgencySlug] = useState("");
  const [boardSettingsLoading, setBoardSettingsLoading] = useState(false);
  const [boardSettingsError, setBoardSettingsError] = useState("");
  const [providerSettings, setProviderSettings] = useState({});
  const evaluationCriteriaEnabled = USE_EVALUATION_CRITERIA;
  const [jobForm, setJobForm] = useState({
    title: "",
    description: "",
    requirements: "",
    minYears: "",
    location: "",
    zipCode: "",
    salary: "",
    salaryMin: "",
    salaryMax: "",
    hourlyPayStyle: "single",
    salaryType: "hourly",
    postedDate: todayInput(),
    expiryDate: plusDaysInput(30),
    isPublishedToBoards: false,
    questions: [""],
    phoneInterviewFocusEnabled: true,
    requirementTags: [],
    requirementFile: null,
    requirementFileName: "",
    requirementFileType: "",
    evaluationCriteria: [],
  });
  const [customRequirementTag, setCustomRequirementTag] = useState("");
  const [customCriteriaLabel, setCustomCriteriaLabel] = useState("");
  const [customCriteriaCategory, setCustomCriteriaCategory] = useState("custom");
  const [publicLinks, setPublicLinks] = useState(null);
  const [publicLinksLoading, setPublicLinksLoading] = useState(false);
  const [publicLinksError, setPublicLinksError] = useState("");
  const [copiedLinkKey, setCopiedLinkKey] = useState(null);
  const successTimeoutRef = useRef(null);

  const inferSalaryType = (salaryType, salaryValue) => {
    const normalized = String(salaryType || "")
      .trim()
      .toLowerCase();
    if (["hourly", "annually", "competitive"].includes(normalized)) {
      return normalized;
    }
    const numeric = Number(salaryValue);
    if (!Number.isFinite(numeric) || numeric <= 0) return "competitive";
    return numeric < 500 ? "hourly" : "annually";
  };

  const formatJobSalaryLabel = (job) => {
    const salaryType = inferSalaryType(job?.salaryType, job?.salary);
    const numeric = Number(job?.salary);
    const min = Number(job?.salaryMin);
    const max = Number(job?.salaryMax);
    if (salaryType === "competitive") {
      return "Competitive Salary";
    }
    if (salaryType === "hourly") {
      if (
        Number.isFinite(min) &&
        Number.isFinite(max) &&
        min > 0 &&
        max > 0
      ) {
        if (min === max) {
          return `$${min.toLocaleString("en-US")}/hr`;
        }
        return `$${min.toLocaleString("en-US")}-$${max.toLocaleString("en-US")}/hr`;
      }
      if (!Number.isFinite(numeric) || numeric <= 0) return "--";
      return `$${numeric.toLocaleString("en-US")}/hr`;
    }
    if (salaryType === "annually") {
      if (
        Number.isFinite(min) &&
        Number.isFinite(max) &&
        min > 0 &&
        max > 0
      ) {
        if (min === max) {
          return `$${min.toLocaleString("en-US")}/yr`;
        }
        return `$${min.toLocaleString("en-US")}-$${max.toLocaleString("en-US")}/yr`;
      }
      if (!Number.isFinite(numeric) || numeric <= 0) return "--";
      return `$${numeric.toLocaleString("en-US")}/yr`;
    }
    if (!Number.isFinite(numeric) || numeric <= 0) return "--";
    return `$${numeric.toLocaleString("en-US")}/yr`;
  };

  const getJobLinkForId = (jobId) => {
    const jobs = publicLinks?.jobs;
    if (!Array.isArray(jobs)) return "";
    const match = jobs.find((j) => String(j?._id) === String(jobId));
    return match?.url || "";
  };

  const getJobApplyUrl = (job) => {
    const fromPublicLinks = getJobLinkForId(job?._id);
    if (fromPublicLinks) return fromPublicLinks;
    return String(job?.publicJobUrl || "").trim();
  };

  const refreshPublicLinks = useCallback(async () => {
    if (!locationId) return;
    setPublicLinksLoading(true);
    setPublicLinksError("");
    try {
      const data = await fetchAgencyPublicLinks(locationId);
      setPublicLinks(data || null);
    } catch (err) {
      setPublicLinks(null);
      setPublicLinksError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load public links.",
      );
    } finally {
      setPublicLinksLoading(false);
    }
  }, [locationId]);

  const getIndeedApplicationLink = () => {
    const jobs = publicLinks?.jobs;
    if (!Array.isArray(jobs) || jobs.length === 0) return "";
    const firstWithUrl = jobs.find((job) => String(job?.url || "").trim());
    return firstWithUrl?.url || "";
  };

  const shortenPublicUrl = (url) => {
    const raw = String(url || "").trim();
    if (!raw) return "";
    try {
      const parsed = new URL(raw);
      const host = parsed.hostname.replace(/^www\./i, "");
      const path = parsed.pathname.replace(/\/+$/, "") || "/";
      return `${host}${path === "/" ? "" : path}`;
    } catch {
      return raw;
    }
  };

  const publicJobPathLabel = (job) => {
    if (job?.slug) return `/job/${job.slug}`;
    const shortened = shortenPublicUrl(job?.url);
    const slash = shortened.indexOf("/");
    return slash >= 0 ? shortened.slice(slash) : shortened;
  };

  const renderPublicLinkActions = (key, url) => (
    <div className="ai-public-links-actions">
      <a
        href={url || undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="ai-public-links-open"
        onClick={(e) => {
          if (!url) e.preventDefault();
        }}
      >
        Open
      </a>
      <button
        type="button"
        className={`ai-button ai-button-sm ${
          copiedLinkKey === key ? "ai-button-success" : "ai-button-secondary"
        }`}
        onClick={() => copyText(key, url)}
        disabled={!url}
      >
        {copiedLinkKey === key ? "Copied" : "Copy"}
      </button>
    </div>
  );

  const connectedProviders = useMemo(
    () =>
      BOARD_PROVIDERS.filter(
        (provider) => Boolean(providerSettings?.[provider]?.isActive),
      ),
    [providerSettings],
  );

  const staticInterviewImagesForPicker = useMemo(() => {
    if (staticInterviewImageOptions.length >= STATIC_INTERVIEW_IMAGE_CATALOG.length) {
      return staticInterviewImageOptions;
    }
    return buildStaticInterviewImageOptions(interviewAppUrl);
  }, [staticInterviewImageOptions, interviewAppUrl]);

  const getFeedUrl = (provider) =>
    agencySlug ? `https://myalbertai.com/feeds/${provider}/${agencySlug}.xml` : "";

  const getWebhookUrl = (provider) =>
    agencySlug
      ? `https://api.myalbertai.com/api/webhooks/job-boards/${provider}/${agencySlug}`
      : "";

  const loadBoardSettings = async (id) => {
    if (!id) return;
    setBoardSettingsLoading(true);
    setBoardSettingsError("");
    try {
      const payload = await fetchAgencyBoardSettings(id);
      const providers = Array.isArray(payload?.providers) ? payload.providers : [];
      const byProvider = BOARD_PROVIDERS.reduce((acc, provider) => {
        const row = providers.find((p) => String(p?.provider) === provider);
        acc[provider] = {
          publisherId: String(row?.publisher_id || ""),
          isActive: Boolean(row?.is_active),
        };
        return acc;
      }, {});
      setProviderSettings(byProvider);
    } catch (err) {
      setBoardSettingsError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load board settings.",
      );
    } finally {
      setBoardSettingsLoading(false);
    }
  };

  const fetchJobBoardStatus = async (jobIds = []) => {
    if (!locationId) return;
    const ids = Array.isArray(jobIds)
      ? jobIds.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
    if (!ids.length) {
      setJobBoardStatus({});
      return;
    }
    try {
      const token = localStorage.getItem("token") || "";
      const response = await fetch(
        `/api/job-board/status?locationId=${encodeURIComponent(locationId)}&jobIds=${encodeURIComponent(ids.join(","))}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to load job board status");
      }
      const payload = await response.json();
      setJobBoardStatus(payload && typeof payload === "object" ? payload : {});
    } catch {
      setJobBoardStatus((prev) => prev || {});
    }
  };

  const loadDistributionStatuses = async (jobIds = []) => {
    if (!locationId) return;
    const ids = Array.isArray(jobIds)
      ? jobIds.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
    if (!ids.length) {
      setDistributionStatusByJob({});
      setDistributionLoadingByJob({});
      return;
    }
    setDistributionLoadingByJob((prev) =>
      ids.reduce((acc, id) => ({ ...acc, [id]: true }), { ...(prev || {}) }),
    );
    const settled = await Promise.allSettled(
      ids.map((id) => fetchAgencyJobDistributionStatus(locationId, id)),
    );
    setDistributionStatusByJob((prev) => {
      const next = { ...(prev || {}) };
      settled.forEach((result, idx) => {
        if (result.status === "fulfilled" && result.value) {
          next[ids[idx]] = result.value;
        }
      });
      return next;
    });
    setDistributionLoadingByJob((prev) => {
      const next = { ...(prev || {}) };
      ids.forEach((id) => {
        next[id] = false;
      });
      return next;
    });
  };

  const setProviderStatus = (jobId, provider, status) => {
    setJobBoardStatus((prev) => ({
      ...(prev || {}),
      [jobId]: {
        ...(prev?.[jobId] || {}),
        [provider]: status,
      },
    }));
  };

  const queueProviderPost = async (jobId, provider) => {
    const key = `${jobId}-${provider}`;
    setPostingError((prev) => ({ ...prev, [key]: "" }));
    setPostingInProgress((prev) => ({ ...prev, [key]: true }));
    setProviderStatus(jobId, provider, "queued");

    try {
      const token = localStorage.getItem("token") || "";
      const response = await fetch(
        `/api/job-board/post?locationId=${encodeURIComponent(locationId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobId, provider }),
        },
      );
      if (!response.ok) {
        throw new Error("Failed to queue");
      }
      await fetchJobBoardStatus([jobId]);
      await loadDistributionStatuses([jobId]);
    } catch {
      setProviderStatus(jobId, provider, null);
      setPostingError((prev) => ({ ...prev, [key]: "Failed to queue" }));
    } finally {
      setPostingInProgress((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleBulkPost = async (provider) => {
    if (!locationId || !Array.isArray(jobs) || !jobs.length) return;
    if (bulkPosting?.[provider]) return;

    const eligibleIds = jobs
      .map((job) => String(job?._id || ""))
      .filter(Boolean)
      .filter((jobId) => {
        const status = jobBoardStatus?.[jobId]?.[provider] ?? null;
        return status === null || status === "failed" || status === "closed";
      });

    const providerLabel = PROVIDER_LABELS[provider] || provider;
    const total = eligibleIds.length;
    setBulkPosting((prev) => ({ ...prev, [provider]: true }));
    setBulkPostingCount((prev) => ({ ...prev, [provider]: total }));

    let successCount = 0;
    for (const jobId of eligibleIds) {
      const key = `${jobId}-${provider}`;
      setPostingInProgress((prev) => ({ ...prev, [key]: true }));
      setPostingError((prev) => ({ ...prev, [key]: "" }));
      setProviderStatus(jobId, provider, "queued");

      try {
        const token = localStorage.getItem("token") || "";
        const response = await fetch(
          `/api/job-board/post?locationId=${encodeURIComponent(locationId)}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ jobId, provider }),
          },
        );
        if (!response.ok) {
          throw new Error("Failed to queue");
        }
        successCount += 1;
      } catch {
        setProviderStatus(jobId, provider, null);
        setPostingError((prev) => ({ ...prev, [key]: "Failed to queue" }));
      } finally {
        setPostingInProgress((prev) => ({ ...prev, [key]: false }));
      }
    }

    await fetchJobBoardStatus(eligibleIds);
    await loadDistributionStatuses(eligibleIds);
    setBulkPosting((prev) => ({ ...prev, [provider]: false }));
    setBulkPostingCount((prev) => ({ ...prev, [provider]: 0 }));
    setSuccess(`Done — ${successCount} jobs submitted for ${providerLabel} review ✓`);
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    successTimeoutRef.current = setTimeout(() => {
      setSuccess("");
    }, 5000);
    if (total === 0) {
      setSuccess(`Done — 0 jobs submitted for ${providerLabel} review ✓`);
    }
  };

  const renderIndeedAction = (jobId, indeedDistributionStatus) => {
    const key = `${jobId}-indeed`;
    const isLoading = !!postingInProgress?.[key];
    const boardStatus = jobBoardStatus?.[jobId]?.indeed ?? null;
    const normalized =
      boardStatus === "closed"
        ? null
        : boardStatus || indeedDistributionStatus || null;

    if (normalized === "posted" || normalized === "queued" || normalized === "pending") {
      return null;
    }

    const label =
      normalized === "failed" ? "Resubmit Review" : "Submit to Indeed";
    const button = (
      <button
        type="button"
        className="ai-button ai-button-sm ai-button-secondary"
        style={{
          padding: "2px 8px",
          borderColor: normalized === "failed" ? "#f59e0b" : "#3b82f6",
          color: normalized === "failed" ? "#c2410c" : "#2563eb",
        }}
        onClick={() => queueProviderPost(jobId, "indeed")}
        disabled={isLoading}
      >
        {isLoading ? "Submitting..." : label}
      </button>
    );

    if (!postingError?.[key]) return button;
    return (
      <>
        {button}
        <span style={{ fontSize: 11, color: "#b91c1c", display: "block", marginTop: 4 }}>
          {postingError[key]}
        </span>
      </>
    );
  };

  useEffect(() => {
    if (!locationId) return;
    let active = true;
    setLoading(true);
    fetchAiHiringSettings(locationId)
      .then((data) => {
        if (!active) return;
        setResumeScore(data?.resumeScreening?.passingScore ?? 10);
        setPhoneScore(data?.phoneInterview?.passingScore ?? 10);
        setVideoScore(data?.videoInterview?.passingScore ?? 10);
        const nextVideoEnabled = data?.videoInterviewEnabled === true;
        setVideoInterviewEnabled(nextVideoEnabled);
        setInitialVideoInterviewEnabled(nextVideoEnabled);
        const nextPhoneEnabled = data?.phoneInterviewEnabled !== false;
        setPhoneInterviewEnabled(nextPhoneEnabled);
        setInitialPhoneInterviewEnabled(nextPhoneEnabled);
        setResumeScreeningRaw(data?.resumeScreening || {});
        setPhoneInterviewRaw(data?.phoneInterview || {});
        setVideoInterviewRaw(data?.videoInterview || {});
        setVideoInterviewProvider(data?.videoInterviewProvider || "openai_realtime");
        setLiveAvatarAgentSelection(data?.liveAvatarAgentSelection || "agent_1");
        setStaticInterviewImageUrl(
          data?.staticInterviewImageUrl || "/interviewers/Kathy1.jpg",
        );
        setStaticInterviewImageOptions(
          Array.isArray(data?.staticInterviewImageOptions)
            ? data.staticInterviewImageOptions
            : [],
        );
        setInterviewAppUrl(data?.interviewAppUrl || DEFAULT_INTERVIEW_APP_URL);
        setAgencyId(String(data?.agencyId || ""));
        setAgencySlug(showTechnicalBoardConfig ? String(data?.agencySlug || "") : "");
        if (showTechnicalBoardConfig && data?.agencyId) {
          void loadBoardSettings(String(data.agencyId));
        }
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load settings.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [locationId]);

  useEffect(() => {
    let active = true;
    fetchLiveAvatarAgentOptions()
      .then((data) => {
        if (!active || !data) return;
        setLiveAvatarAgentOptions((prev) => ({
          agent_1: { ...prev.agent_1, ...(data.agent_1 || {}) },
          agent_2: { ...prev.agent_2, ...(data.agent_2 || {}) },
        }));
      })
      .catch(() => {
        // Non-fatal — the picker still renders with plain "Avatar 1"/
        // "Avatar 2" labels and no preview image if this fails.
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!locationId) return;
    let active = true;
    const loadJobs = async () => {
      setJobsLoading(true);
      setJobsError("");
      try {
        const data = await fetchAgencyJobs(locationId);
        if (!active) return;
        const nextJobs = Array.isArray(data) ? data : [];
        setJobs(nextJobs);
        const ids = nextJobs.map((job) => String(job?._id || "")).filter(Boolean);
        await fetchJobBoardStatus(ids);
        await loadDistributionStatuses(ids);
      } catch (err) {
        if (!active) return;
        setJobs([]);
        setJobBoardStatus({});
        setDistributionStatusByJob({});
        setJobsError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load jobs.",
        );
      } finally {
        if (active) setJobsLoading(false);
      }
    };
    loadJobs();
    return () => {
      active = false;
    };
  }, [locationId]);

  useEffect(() => {
    void refreshPublicLinks();
  }, [refreshPublicLinks]);

  const handleSave = async () => {
    if (!locationId) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const saved = await updateAiHiringSettings(locationId, {
        // Spread the raw fetched settings first so fields this page's UI
        // doesn't edit round-trip unchanged instead of getting dropped.
        resumeScreening: {
          ...resumeScreeningRaw,
          passingScore: Number(resumeScore),
        },
        phoneInterview: {
          ...phoneInterviewRaw,
          passingScore: Number(phoneScore),
        },
        videoInterview: {
          ...videoInterviewRaw,
          passingScore: Number(videoScore),
          staticInterviewImageUrl,
        },
        videoInterviewEnabled: Boolean(videoInterviewEnabled),
        phoneInterviewEnabled: Boolean(phoneInterviewEnabled),
        liveAvatarAgentSelection,
      });
      if (saved?.staticInterviewImageUrl) {
        setStaticInterviewImageUrl(saved.staticInterviewImageUrl);
      }
      if (saved?.videoInterview) {
        setVideoInterviewRaw(saved.videoInterview);
      }
      if (Array.isArray(saved?.staticInterviewImageOptions)) {
        setStaticInterviewImageOptions(saved.staticInterviewImageOptions);
      }
      const videoToggleChanged =
        Boolean(videoInterviewEnabled) !== Boolean(initialVideoInterviewEnabled);
      const phoneToggleChanged =
        Boolean(phoneInterviewEnabled) !== Boolean(initialPhoneInterviewEnabled);
      setInitialVideoInterviewEnabled(Boolean(videoInterviewEnabled));
      setInitialPhoneInterviewEnabled(Boolean(phoneInterviewEnabled));
      setSuccess(
        videoToggleChanged
          ? videoInterviewEnabled
            ? "Video interview stage enabled."
            : "Video interview stage disabled."
          : phoneToggleChanged
            ? phoneInterviewEnabled
              ? "Phone interview stage enabled."
              : "Phone interview stage disabled."
            : "Saved successfully.",
      );
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess("");
      }, 5000);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save settings.",
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const resetJobForm = () => {
    setJobEditingId(null);
    setPostToIndeed(false);
    setPostToZipRecruiter(false);
    setJobForm({
      title: "",
      description: "",
      requirements: "",
      minYears: "",
      location: "",
      zipCode: "",
      salary: "",
      salaryMin: "",
      salaryMax: "",
      hourlyPayStyle: "single",
      salaryType: "hourly",
      postedDate: todayInput(),
      expiryDate: plusDaysInput(30),
      isPublishedToBoards: false,
      questions: [""],
      phoneInterviewFocusEnabled: true,
      requirementTags: [],
      requirementFile: null,
      requirementFileName: "",
      requirementFileType: "",
      evaluationCriteria: buildDefaultEvaluationCriteria(),
    });
    setCustomRequirementTag("");
    setCustomCriteriaLabel("");
    setCustomCriteriaCategory("custom");
  };

  const openCreateJob = () => {
    resetJobForm();
    setJobModalOpen(true);
  };

  const openEditJob = (job) => {
    setJobEditingId(job?._id || null);
    setJobForm({
      title: job?.title || "",
      description: job?.description || "",
      requirements: job?.requirements || "",
      minYears:
        job?.minYears === null || job?.minYears === undefined
          ? ""
          : String(job.minYears),
      location: job?.location || "",
      zipCode: job?.zipCode || "",
      salary:
        job?.salary === null || job?.salary === undefined ? "" : String(job.salary),
      salaryMin:
        job?.salaryMin === null || job?.salaryMin === undefined
          ? ""
          : String(job.salaryMin),
      salaryMax:
        job?.salaryMax === null || job?.salaryMax === undefined
          ? ""
          : String(job.salaryMax),
      hourlyPayStyle:
        Number(job?.salaryMin) > 0 && Number(job?.salaryMax) > 0 ? "range" : "single",
      salaryType: inferSalaryType(job?.salaryType, job?.salary),
      postedDate: toInputDate(job?.postedDate || job?.createdAt || new Date()),
      expiryDate: toInputDate(job?.expiryDate || ""),
      isPublishedToBoards: Boolean(
        job?.isPublishedToBoards ?? job?.is_published_to_boards,
      ),
      questions:
        Array.isArray(job?.questions) && job.questions.length
          ? job.questions
          : [""],
      phoneInterviewFocusEnabled: job?.phoneInterviewFocusEnabled !== false,
      requirementTags: Array.isArray(job?.requirementTags) ? job.requirementTags : [],
      requirementFile: null,
      requirementFileName: String(job?.requirementFile?.name || ""),
      requirementFileType: String(job?.requirementFile?.type || ""),
      // Jobs created before this feature existed have no evaluationCriteria
      // saved yet — seed from the library defaults so there's still a
      // checklist to review/toggle instead of an empty section.
      evaluationCriteria:
        Array.isArray(job?.evaluationCriteria) && job.evaluationCriteria.length
          ? job.evaluationCriteria
          : buildDefaultEvaluationCriteria(),
    });
    setCustomRequirementTag("");
    setCustomCriteriaLabel("");
    setCustomCriteriaCategory("custom");
    setJobModalOpen(true);
  };

  const toggleRequirementTag = (tagValue) => {
    const tag = String(tagValue || "")
      .trim()
      .toLowerCase();
    if (!tag) return;
    setJobForm((prev) => {
      const current = Array.isArray(prev.requirementTags) ? prev.requirementTags : [];
      if (current.includes(tag)) {
        return { ...prev, requirementTags: current.filter((item) => item !== tag) };
      }
      return { ...prev, requirementTags: [...current, tag] };
    });
  };

  const addCustomRequirementTag = () => {
    const tag = String(customRequirementTag || "")
      .trim()
      .toLowerCase();
    if (!tag) return;
    setJobForm((prev) => {
      const current = Array.isArray(prev.requirementTags) ? prev.requirementTags : [];
      if (current.includes(tag)) return prev;
      return { ...prev, requirementTags: [...current, tag] };
    });
    setCustomRequirementTag("");
  };

  const toggleEvaluationCriteria = (criteriaId) => {
    setJobForm((prev) => {
      const current = Array.isArray(prev.evaluationCriteria) ? prev.evaluationCriteria : [];
      return {
        ...prev,
        evaluationCriteria: current.map((item) =>
          item.criteriaId === criteriaId ? { ...item, isActive: !item.isActive } : item,
        ),
      };
    });
  };

  const addCustomEvaluationCriteria = () => {
    const label = String(customCriteriaLabel || "").trim();
    if (!label) return;
    const criteriaId = `custom_${label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")}_${Date.now().toString(36)}`;
    setJobForm((prev) => {
      const current = Array.isArray(prev.evaluationCriteria) ? prev.evaluationCriteria : [];
      return {
        ...prev,
        evaluationCriteria: [
          ...current,
          {
            criteriaId,
            label,
            category: customCriteriaCategory || "custom",
            source: "any",
            isCustom: true,
            isActive: true,
          },
        ],
      };
    });
    setCustomCriteriaLabel("");
  };

  const removeEvaluationCriteria = (criteriaId) => {
    setJobForm((prev) => {
      const current = Array.isArray(prev.evaluationCriteria) ? prev.evaluationCriteria : [];
      return {
        ...prev,
        evaluationCriteria: current.filter((item) => item.criteriaId !== criteriaId),
      };
    });
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const commaIdx = result.indexOf(",");
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      };
      reader.onerror = () => reject(new Error("Failed to read file."));
      reader.readAsDataURL(file);
    });

  const handleSaveJob = async () => {
    if (!locationId) return;
    const title = (jobForm.title || "").trim();
    if (!title) {
      setJobsError("Job title is required.");
      return;
    }
    const zipDigits = String(jobForm.zipCode || "").replace(/\D/g, "");
    const normalizedZip =
      zipDigits.length >= 9
        ? `${zipDigits.slice(0, 5)}-${zipDigits.slice(5, 9)}`
        : zipDigits.slice(0, 5);
    if (!/^\d{5}(-\d{4})?$/.test(normalizedZip)) {
      setJobsError("Zip code is required (enter one 5-digit US zip code).");
      return;
    }
    const postedDateInput = String(jobForm.postedDate || "").trim();
    const expiryDateInput = String(jobForm.expiryDate || "").trim();
    if (!postedDateInput) {
      setJobsError("Posted Date is required.");
      return;
    }
    if (
      expiryDateInput &&
      new Date(`${expiryDateInput}T23:59:59.999Z`).getTime() <=
        new Date(`${postedDateInput}T00:00:00.000Z`).getTime()
    ) {
      setJobsError("Application Deadline must be after Posted Date.");
      return;
    }
    const isHourlyRange =
      jobForm.salaryType === "hourly" && jobForm.hourlyPayStyle === "range";
    const isAnnualRange =
      jobForm.salaryType === "annually" && jobForm.hourlyPayStyle === "range";
    if (isHourlyRange || isAnnualRange) {
      const min = parsePayValue(jobForm.salaryMin);
      const max = parsePayValue(jobForm.salaryMax);
      const payLabel = isHourlyRange ? "hourly pay" : "annual salary";
      if (!min || !max) {
        setJobsError(`Enter both minimum and maximum ${payLabel} for the range.`);
        return;
      }
      if (max < min) {
        setJobsError(
          `Maximum ${payLabel} must be greater than or equal to minimum.`,
        );
        return;
      }
    } else if (
      jobForm.salaryType !== "competitive" &&
      !String(jobForm.salary || "").trim()
    ) {
      setJobsError("Enter a salary amount or choose Competitive Salary.");
      return;
    }
    setJobSaving(true);
    setJobsError("");
    try {
      const isCompetitive = jobForm.salaryType === "competitive";
      const isPayRange =
        (jobForm.salaryType === "hourly" || jobForm.salaryType === "annually") &&
        jobForm.hourlyPayStyle === "range";
      const salaryMin = isPayRange ? parsePayValue(jobForm.salaryMin) : null;
      const salaryMax = isPayRange ? parsePayValue(jobForm.salaryMax) : null;
      const singleSalary = parsePayValue(jobForm.salary);
      const payload = {
        title,
        description: jobForm.description || "",
        requirements: jobForm.requirements || "",
        minYears:
          jobForm.minYears === "" || jobForm.minYears === null
            ? null
            : Number(jobForm.minYears),
        location: jobForm.location || "",
        zipCode: normalizedZip,
        hiringCount: 1,
        salary: isCompetitive
          ? null
          : isPayRange
            ? salaryMin || salaryMax
            : singleSalary,
        salaryMin: isPayRange ? salaryMin : null,
        salaryMax: isPayRange ? salaryMax : null,
        salaryType: jobForm.salaryType || "hourly",
        postedDate: postedDateInput,
        expiryDate: expiryDateInput || null,
        isPublishedToBoards: true,
        questions: Array.isArray(jobForm.questions)
          ? jobForm.questions.map((q) => String(q || "").trim()).filter(Boolean)
          : [],
        phoneInterviewFocusEnabled: Boolean(jobForm.phoneInterviewFocusEnabled),
        requirementTags: Array.isArray(jobForm.requirementTags)
          ? jobForm.requirementTags
              .map((tag) => String(tag || "").trim().toLowerCase())
              .filter(Boolean)
          : [],
        evaluationCriteria: evaluationCriteriaEnabled
          ? Array.isArray(jobForm.evaluationCriteria)
            ? jobForm.evaluationCriteria
            : []
          : undefined,
      };
      if (jobForm.requirementFile) {
        payload.requirementFile = {
          name: String(jobForm.requirementFile.name || "").trim(),
          type: String(jobForm.requirementFile.type || "").trim(),
          data: await readFileAsBase64(jobForm.requirementFile),
        };
      } else if (jobEditingId && jobForm.requirementFileName) {
        // keep existing file untouched on edit if user doesn't upload a new one
      }

      const saved = jobEditingId
        ? await updateAgencyJob(locationId, jobEditingId, payload)
        : await createAgencyJob(locationId, payload);

      setJobs((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex((item) => item?._id === saved?._id);
        if (idx >= 0) {
          next[idx] = saved;
          return next;
        }
        return [saved, ...next];
      });
      if (saved?._id) {
        await fetchJobBoardStatus([String(saved._id)]);
        await loadDistributionStatuses([String(saved._id)]);
        await refreshPublicLinks();
      }
      if (!jobEditingId) {
        const token = localStorage.getItem("token") || "";
        const postUrl = `/api/job-board/post?locationId=${encodeURIComponent(locationId)}`;

        fetch(postUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobId: saved._id, provider: "indeed" }),
        }).catch((err) => console.error("Indeed post failed", err));

        if (postToZipRecruiter) {
          fetch(postUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ jobId: saved._id, provider: "ziprecruiter" }),
          }).catch((err) => console.error("ZipRecruiter post failed", err));
        }

        if (postToZipRecruiter && showTechnicalBoardConfig) {
          setSuccess("Job saved. Apply link is ready — Indeed review may take a few minutes ✓");
        } else {
          setSuccess("Job saved. Apply link is ready — Indeed review may take a few minutes ✓");
        }
      } else {
        setSuccess("Job updated and posted.");
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess("");
      }, 5000);
      setJobModalOpen(false);
      resetJobForm();
    } catch (err) {
      setJobsError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save job.",
      );
    } finally {
      setJobSaving(false);
    }
  };

  const handleForceRefreshFeed = async () => {
    setFeedRefreshing(true);
    setJobsError("");
    try {
      await invalidateIndeedFeedCache(locationId);
      setSuccess("Indeed feed cache invalidated and refresh ping sent.");
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setJobsError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to refresh feed cache.",
      );
    } finally {
      setFeedRefreshing(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!locationId || !jobId) return;
    const ok = window.confirm("Delete this job? This cannot be undone.");
    if (!ok) return;
    try {
      await deleteAgencyJob(locationId, jobId);
      setJobs((prev) => (Array.isArray(prev) ? prev.filter((j) => j?._id !== jobId) : []));
      setDistributionStatusByJob((prev) => {
        const next = { ...(prev || {}) };
        delete next[String(jobId)];
        return next;
      });
      setSuccess("Job deleted.");
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess("");
      }, 5000);
    } catch (err) {
      setJobsError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete job.",
      );
    }
  };

  const copyText = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLinkKey(key);
      setTimeout(() => setCopiedLinkKey(null), 1500);
    } catch {
      // fallback for older browsers
      try {
        const url = text;
        const el = document.createElement("textarea");
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        setCopiedLinkKey(key);
        setTimeout(() => setCopiedLinkKey(null), 1500);
      } catch {
        setJobsError("Unable to copy link.");
      }
    }
  };

  return (
    <div className="page-shell ai-hiring-page">
      <PageHeader
        title="AI Hiring - Interview Settings"
        subtitle="Manage interview stages, job criteria, and hiring prompts."
        icon={
          <span role="img" aria-label="Settings">
            ⚙️
          </span>
        }
        {...props}
      />

      <div className="ai-hiring-content">
        {success && <div className="ai-alert ai-alert-success">{success}</div>}
        {error && <div className="ai-alert ai-alert-error">{error}</div>}
        {!locationId ? (
          <div className="ai-empty">Select a location to edit settings.</div>
        ) : loading ? (
          <div className="ai-empty">Loading settings...</div>
        ) : (
          <>
            <div className="ai-card ai-settings-hero">
              <div>
                <p className="ai-card-note">Interview Configuration</p>
                <h2 className="ai-settings-title">
                  Customize how your AI hires
                </h2>
                <p className="ai-settings-subtitle">
                  {evaluationCriteriaEnabled
                    ? "Candidates are checked against your per-job Evaluation Criteria checklist instead of numeric scores. Guide the AI below on what matters most for your agency."
                    : "Set passing thresholds and guide the AI on what matters most for your agency. Changes apply immediately to new interviews."}
                </p>
              </div>
              {!evaluationCriteriaEnabled ? (
                <div className="ai-settings-pill-row">
                  <div className="ai-settings-pill">
                    <span>Resume score</span>
                    <strong>{resumeScore}</strong>
                  </div>
                  <div className="ai-settings-pill">
                    <span>Phone score</span>
                    <strong>{phoneScore}</strong>
                  </div>
                  <div className="ai-settings-pill">
                    <span>Video score</span>
                    <strong>{videoScore}</strong>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="ai-settings-grid">
              {!evaluationCriteriaEnabled ? (
                <div className="ai-card ai-settings-card">
                  <div className="ai-settings-card-header">
                    <div>
                      <p className="ai-card-note">Passing Scores</p>
                      <h3 className="ai-card-title">Interview thresholds</h3>
                    </div>
                  </div>
                  <div className="ai-settings-fields">
                    <div className="ai-form-group">
                      <label className="ai-label">
                        Application &amp; Resume Screening Score
                      </label>
                      <div className="ai-score-field">
                        <input
                          className="ai-input ai-score-input"
                          type="number"
                          value={resumeScore}
                          onChange={(e) => setResumeScore(e.target.value)}
                          min="0"
                          max="100"
                        />
                        <span className="ai-score-suffix">/100</span>
                      </div>
                      <p className="ai-help">
                        Keep low (10-30) so most candidates reach the phone interview.
                      </p>
                    </div>

                    <div className="ai-form-group">
                      <label className="ai-label">
                        Phone Interview Passing Score
                      </label>
                      <div className="ai-score-field">
                        <input
                          className="ai-input ai-score-input"
                          type="number"
                          value={phoneScore}
                          onChange={(e) => setPhoneScore(e.target.value)}
                          min="0"
                          max="100"
                        />
                        <span className="ai-score-suffix">/100</span>
                      </div>
                      <p className="ai-help">
                        Recommended range: 40-70 for caregiver roles.
                      </p>
                    </div>

                    {videoInterviewEnabled ? (
                      <div className="ai-form-group">
                        <label className="ai-label">
                          Video Interview Passing Score
                        </label>
                        <div className="ai-score-field">
                          <input
                            className="ai-input ai-score-input"
                            type="number"
                            value={videoScore}
                            onChange={(e) => setVideoScore(e.target.value)}
                            min="0"
                            max="100"
                          />
                          <span className="ai-score-suffix">/100</span>
                        </div>
                        <p className="ai-help">
                          Recommended range: 40-70 for caregiver roles.
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="ai-card ai-video-stage-card">
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Phone Interview Stage</p>
                  <h3 className="ai-card-title">Phone Interview</h3>
                </div>
              </div>
              <div
                className={`ai-video-toggle-shell ${
                  phoneInterviewEnabled
                    ? "ai-video-toggle-shell-enabled"
                    : "ai-video-toggle-shell-disabled"
                }`}
              >
                <div className="ai-video-toggle-copy">
                  <p className="ai-video-toggle-title">📞 Phone Interview</p>
                  <p className="ai-video-toggle-subtitle">
                    Candidates who apply will automatically receive an AI
                    phone interview call within minutes of applying. This is
                    an alternate to Video Interview — only one stage is active
                    for your agency at a time.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={
                    phoneInterviewEnabled
                      ? "Disable phone interview stage"
                      : "Enable phone interview stage"
                  }
                  className={`ai-switch ${phoneInterviewEnabled ? "on" : "off"}`}
                  onClick={() =>
                    setPhoneInterviewEnabled((prev) => {
                      const next = !prev;
                      // Phone and video are mutually-exclusive alternate
                      // paths — turning one on immediately turns the other
                      // off, mirroring a radio button. Persisted on Save.
                      if (next) setVideoInterviewEnabled(false);
                      return next;
                    })
                  }
                >
                  <span className="ai-switch-knob" />
                </button>
              </div>

              <div className="ai-video-state-grid">
                <div className="ai-video-state-block">
                  <p className="ai-video-state-title">When ENABLED:</p>
                  <p>✓ Candidates receive an automatic AI phone interview</p>
                  <p>✓ Responses are checked against your job&apos;s Evaluation Criteria checklist</p>
                  <p>✓ Qualified candidates move to your review queue (video is disabled while phone is active)</p>
                </div>
                <div className="ai-video-state-block">
                  <p className="ai-video-state-title">When DISABLED:</p>
                  <p>✓ No automatic phone call is placed</p>
                  <p>✓ Candidate stays pending for manual recruiter review, unless Video Interview is enabled instead</p>
                </div>
              </div>

              <p className="ai-help">
                Phone and Video are alternate interview types — only one can be active at a time. Changes apply to new applicants.
              </p>
            </div>

            <div className="ai-card ai-video-stage-card">
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Video Interview Stage</p>
                  <h3 className="ai-card-title">Video Interview</h3>
                </div>
              </div>
              <div
                className={`ai-video-toggle-shell ${
                  videoInterviewEnabled
                    ? "ai-video-toggle-shell-enabled"
                    : "ai-video-toggle-shell-disabled"
                }`}
              >
                <div className="ai-video-toggle-copy">
                  <p className="ai-video-toggle-title">🎥 Video Interview</p>
                  <p className="ai-video-toggle-subtitle">
                    Candidates who apply and pass resume screening will be
                    invited directly to a video interview — this is an
                    alternate to Phone Interview, not a follow-on stage after
                    it. Only one stage is active for your agency at a time.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={
                    videoInterviewEnabled
                      ? "Disable video interview stage"
                      : "Enable video interview stage"
                  }
                  className={`ai-switch ${videoInterviewEnabled ? "on" : "off"}`}
                  onClick={() =>
                    setVideoInterviewEnabled((prev) => {
                      const next = !prev;
                      if (next) setPhoneInterviewEnabled(false);
                      return next;
                    })
                  }
                >
                  <span className="ai-switch-knob" />
                </button>
              </div>

              <div className="ai-video-state-grid">
                <div className="ai-video-state-block">
                  <p className="ai-video-state-title">When ENABLED:</p>
                  <p>✓ Candidates receive a direct video interview invite after apply</p>
                  <p>✓ Responses are checked against your job&apos;s Evaluation Criteria checklist</p>
                  <p>✓ Qualified candidates move to your review queue</p>
                </div>
                <div className="ai-video-state-block">
                  <p className="ai-video-state-title">When DISABLED:</p>
                  <p>✓ No automatic video invite is sent</p>
                  <p>✓ Phone Interview is the active stage instead (if enabled)</p>
                </div>
              </div>

              <div className="ai-video-pipeline">
                <p className="ai-video-state-title" style={{ marginBottom: 10 }}>
                  Hiring pipeline
                </p>
                <p className="ai-help" style={{ marginTop: 0, marginBottom: 10 }}>
                  Phone and Video are alternate paths — exactly one is active
                  at a time, not sequential stages.
                </p>
                <div className="ai-video-pipeline-row">
                  <span className="ai-pipeline-step active">Apply</span>
                  <span className="ai-pipeline-arrow">→</span>
                  <span className="ai-pipeline-step active">Resume review</span>
                  <span className="ai-pipeline-arrow">→</span>
                  {phoneInterviewEnabled ? (
                    <span className="ai-pipeline-step active">Phone interview</span>
                  ) : videoInterviewEnabled ? (
                    <span className="ai-pipeline-step active">Video interview</span>
                  ) : (
                    <span className="ai-pipeline-step muted">
                      No interview stage enabled
                    </span>
                  )}
                  <span className="ai-pipeline-arrow">→</span>
                  <span
                    className={`ai-pipeline-step ${
                      phoneInterviewEnabled || videoInterviewEnabled
                        ? "active"
                        : "muted"
                    }`}
                  >
                    Review queue
                  </span>
                </div>
              </div>

              <p className="ai-help">Changes apply to new applicants.</p>
            </div>

            <div className="ai-card">
              <div className="ai-card-header">
                <div>
                  <h3 className="ai-card-title">Interviewer Avatar</h3>
                </div>
              </div>
              {videoInterviewProvider === "static_interview" ? (
                <>
                  <p className="ai-help" style={{ marginTop: -4, marginBottom: 14 }}>
                    Choose which still photo appears as the AI interviewer during video
                    interviews. Applies when Video AI Settings is set to Static Interview.
                  </p>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {staticInterviewImagesForPicker.map((option) => {
                      const isSelected = staticInterviewImageUrl === option.url;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => setStaticInterviewImageUrl(option.url)}
                          style={{
                            width: 160,
                            padding: 12,
                            borderRadius: 12,
                            border: isSelected ? "2px solid #2563eb" : "1px solid #d1d5db",
                            background: isSelected ? "#eff6ff" : "#fff",
                            cursor: "pointer",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: 120,
                              borderRadius: 8,
                              background: "#f1f5f9",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginBottom: 8,
                            }}
                          >
                            {option.previewUrl ? (
                              <img
                                src={option.previewUrl}
                                alt={option.name}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <span style={{ fontSize: 12, color: "#94a3b8" }}>No preview</span>
                            )}
                          </div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{option.name}</div>
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                            {option.azureVoiceGender === "male" ? "Male voice" : "Female voice"}
                          </div>
                          {isSelected && (
                            <div style={{ fontSize: 11, color: "#2563eb", marginTop: 2 }}>
                              Selected
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : videoInterviewProvider === "openai_realtime" ||
                videoInterviewProvider === "elevenlabs_liveavatar" ? (
                <>
                  <p className="ai-help" style={{ marginTop: -4, marginBottom: 14 }}>
                    {videoInterviewProvider === "openai_realtime"
                      ? "Choose which animated LiveAvatar interviewer candidates see during OpenAI Realtime video interviews."
                      : "Choose which animated LiveAvatar interviewer candidates see during legacy ElevenLabs + LiveAvatar interviews."}
                  </p>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {["agent_1", "agent_2"].map((optionKey) => {
                      const option = liveAvatarAgentOptions[optionKey] || {};
                      const isConfigured = option.configured !== false;
                      const isSelected = liveAvatarAgentSelection === optionKey;
                      const fallbackLabel =
                        optionKey === "agent_1" ? "Kathy Caregiver" : "Kathy Nurse";
                      return (
                        <button
                          key={optionKey}
                          type="button"
                          disabled={!isConfigured}
                          onClick={() => isConfigured && setLiveAvatarAgentSelection(optionKey)}
                          style={{
                            width: 160,
                            padding: 12,
                            borderRadius: 12,
                            border: isSelected ? "2px solid #2563eb" : "1px solid #d1d5db",
                            background: isSelected ? "#eff6ff" : "#fff",
                            cursor: isConfigured ? "pointer" : "not-allowed",
                            opacity: isConfigured ? 1 : 0.5,
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: 120,
                              borderRadius: 8,
                              background: "#f1f5f9",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginBottom: 8,
                            }}
                          >
                            {option.previewUrl ? (
                              <img
                                src={option.previewUrl}
                                alt={option.name || fallbackLabel}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                                {isConfigured ? "No preview" : "Not set up yet"}
                              </span>
                            )}
                          </div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>
                            {option.name || fallbackLabel}
                          </div>
                          {isSelected && (
                            <div style={{ fontSize: 11, color: "#2563eb", marginTop: 2 }}>
                              Selected
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="ai-help" style={{ marginTop: -4, marginBottom: 14 }}>
                  Interviewer avatar selection is not available for the current video interview
                  mode. Contact your administrator to change Video AI Settings.
                </p>
              )}
            </div>

            <p className="ai-help">
              Manage your AI Hiring plan under{" "}
              <a href="/payment-billing">Payment &amp; Billing</a>.
            </p>

            <div className="ai-card">
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Job Roles</p>
                  <h3 className="ai-card-title">
                    Create job-specific interview links
                  </h3>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {showTechnicalBoardConfig ? (
                    <>
                      <button
                        type="button"
                        className="ai-button ai-button-sm ai-button-secondary"
                        onClick={handleForceRefreshFeed}
                        disabled={feedRefreshing}
                      >
                        {feedRefreshing ? "Refreshing..." : "Force Refresh Feed"}
                      </button>
                      {connectedProviders.map((provider) => (
                        <button
                          key={`bulk-${provider}`}
                          type="button"
                          className="ai-button ai-button-sm ai-button-secondary"
                          style={{ borderColor: "#3b82f6", color: "#2563eb" }}
                          onClick={() => handleBulkPost(provider)}
                          disabled={!locationId || bulkPosting?.[provider] || jobsLoading}
                        >
                          {`Post all to ${PROVIDER_LABELS[provider] || provider}`}
                        </button>
                      ))}
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="ai-button ai-button-primary"
                    onClick={openCreateJob}
                    disabled={!locationId}
                  >
                    Create Job
                  </button>
                </div>
              </div>
              {showTechnicalBoardConfig
                ? connectedProviders
                .filter((provider) => bulkPosting?.[provider])
                .map((provider) => (
                  <p key={`posting-${provider}`} className="ai-help" style={{ marginTop: 8 }}>
                    {`Submitting ${bulkPostingCount?.[provider] || 0} jobs for ${
                      PROVIDER_LABELS[provider] || provider
                    } review...`}
                  </p>
                ))
                : null}

              {jobsError && (
                <div className="ai-alert ai-alert-error">{jobsError}</div>
              )}

              <div className="ai-table-wrapper">
                <table className="ai-table" style={{ minWidth: 860, tableLayout: "auto" }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 150 }}>Title</th>
                      <th style={{ minWidth: 120 }}>Location</th>
                      <th style={{ minWidth: 80 }}>Salary</th>
                      <th style={{ minWidth: 110 }}>Posted Date</th>
                      <th style={{ minWidth: 140 }}>Status</th>
                      <th style={{ minWidth: 110 }}>Link</th>
                      <th style={{ minWidth: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobsLoading ? (
                      <tr>
                        <td colSpan="7">Loading jobs...</td>
                      </tr>
                    ) : jobs.length === 0 ? (
                      <tr>
                        <td colSpan="7">
                          No jobs yet. Create a job to generate a unique apply
                          link with custom questions.
                        </td>
                      </tr>
                    ) : (
                      jobs.map((job) => {
                        const url = getJobApplyUrl(job);
                        const key = `jobRole:${job?._id}`;
                        const jobId = String(job?._id || "");
                        const distribution = distributionStatusByJob?.[jobId] || null;
                        const loadingDistribution = Boolean(distributionLoadingByJob?.[jobId]);
                        const homecareStatus = String(
                          distribution?.homecareInterview?.status || "pending",
                        ).toLowerCase();
                        const indeedDistributionStatus = String(
                          distribution?.indeed?.status ||
                            jobBoardStatus?.[jobId]?.indeed ||
                            "pending",
                        ).toLowerCase();
                        const postedDate = formatDisplayDate(
                          job?.postedDate || job?.createdAt,
                        );
                        const isExpired = isExpiredByDate(job?.expiryDate);
                        const boardStatus = String(
                          job?.boardStatus || job?.board_status || "draft",
                        ).toLowerCase();
                        const lifecycleMeta = getJobLifecycleMeta({
                          boardStatus,
                          homecareStatus,
                          indeedStatus: indeedDistributionStatus,
                          isExpired,
                        });
                        return (
                          <tr key={job._id}>
                            <td title={job.title || ""} style={{ minWidth: 150 }}>
                              {job.title || "--"}
                            </td>
                            <td title={job.location || ""} style={{ minWidth: 120 }}>
                              {job.location || "--"}
                            </td>
                            <td title={formatJobSalaryLabel(job)} style={{ minWidth: 80 }}>
                              {formatJobSalaryLabel(job)}
                            </td>
                            <td title={postedDate} style={{ minWidth: 110 }}>
                              {postedDate}
                            </td>
                            <td style={{ minWidth: 140 }}>
                              {loadingDistribution ? (
                                <span style={{ fontSize: 11, color: "var(--ai-muted)" }}>
                                  Updating...
                                </span>
                              ) : (
                                <>
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      minWidth: 84,
                                      padding: "4px 10px",
                                      borderRadius: 999,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      background: lifecycleMeta.background,
                                      color: lifecycleMeta.color,
                                      border: lifecycleMeta.border,
                                    }}
                                    title={lifecycleMeta.hint || ""}
                                  >
                                    {lifecycleMeta.label}
                                  </span>
                                  {lifecycleMeta.hint ? (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: "var(--ai-muted)",
                                        marginTop: 4,
                                        maxWidth: 140,
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {lifecycleMeta.hint}
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </td>
                            <td style={{ minWidth: 110 }}>
                              <button
                                type="button"
                                className={`ai-button ai-button-sm ${
                                  copiedLinkKey === key
                                    ? "ai-button-success"
                                    : "ai-button-secondary"
                                }`}
                                onClick={() => copyText(key, url)}
                                disabled={!url}
                                title={url}
                              >
                                {copiedLinkKey === key ? "Copied" : "Copy Link"}
                              </button>
                            </td>
                            <td
                              style={{
                                minWidth: 120,
                                verticalAlign: "top",
                                whiteSpace: "normal",
                              }}
                            >
                              <div className="ai-decision-actions">
                                <button
                                  type="button"
                                  className="ai-button ai-button-sm ai-button-secondary"
                                  onClick={() => openEditJob(job)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="ai-button ai-button-sm ai-button-danger"
                                  onClick={() => handleDeleteJob(job._id)}
                                >
                                  Delete
                                </button>
                                {renderIndeedAction(jobId, indeedDistributionStatus)}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="ai-card">
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Public Hiring Links</p>
                  <h3 className="ai-card-title">
                    Share these links with candidates.
                  </h3>
                </div>
              </div>

              {publicLinksError && (
                <div className="ai-alert ai-alert-error">{publicLinksError}</div>
              )}

              {publicLinksLoading ? (
                <div className="ai-empty">Loading public links...</div>
              ) : !publicLinks?.companyLink ? (
                <div className="ai-empty">
                  Public links are not available yet.
                </div>
              ) : (
                <div className="ai-public-links">
                  <div className="ai-public-links-featured">
                    <div className="ai-public-links-featured-mark" aria-hidden="true">
                      Co
                    </div>
                    <div className="ai-public-links-featured-body">
                      <span className="ai-public-links-kicker">Company careers page</span>
                      <h4 className="ai-public-links-featured-title">
                        All open positions
                      </h4>
                      <p
                        className="ai-public-links-path"
                        title={publicLinks.companyLink}
                      >
                        {shortenPublicUrl(publicLinks.companyLink)}
                      </p>
                    </div>
                    {renderPublicLinkActions("company", publicLinks.companyLink)}
                  </div>

                  <div className="ai-public-links-section">
                    <div className="ai-public-links-section-head">
                      <h4 className="ai-public-links-section-title">
                        Job application links
                      </h4>
                      <span className="ai-public-links-count">
                        {Array.isArray(publicLinks.jobs)
                          ? publicLinks.jobs.length
                          : 0}{" "}
                        {publicLinks.jobs?.length === 1 ? "job" : "jobs"}
                      </span>
                    </div>

                    {!Array.isArray(publicLinks.jobs) ||
                    publicLinks.jobs.length === 0 ? (
                      <div className="ai-public-links-empty">
                        No jobs yet. Create a job above to generate apply links.
                      </div>
                    ) : (
                      <div className="ai-public-links-jobs">
                        <div className="ai-public-links-jobs-head">
                          <span>Job</span>
                          <span>Apply path</span>
                          <span>Actions</span>
                        </div>
                        {publicLinks.jobs.map((job) => {
                          const key = `jobLink:${job?._id || job?.slug || job?.url}`;
                          const title =
                            String(job?.title || "").trim() || "Untitled job";
                          return (
                            <div key={key} className="ai-public-links-job-row">
                              <div
                                className="ai-public-links-job-title"
                                title={title}
                              >
                                {title}
                              </div>
                              <code
                                className="ai-public-links-job-path"
                                title={job?.url || ""}
                              >
                                {publicJobPathLabel(job) || "--"}
                              </code>
                              {renderPublicLinkActions(key, job?.url)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="ai-public-links-channels">
                    <div className="ai-public-links-channel">
                      <span className="ai-public-links-kicker">
                        Recruiting channels
                      </span>
                      <h4 className="ai-public-links-channel-title">
                        Live hiring link (Indeed)
                      </h4>
                      <p className="ai-public-links-channel-desc">
                        Share with job boards so candidates apply through your
                        Albert hiring flow.
                      </p>
                      <p
                        className="ai-public-links-path"
                        title={getIndeedApplicationLink() || ""}
                      >
                        {shortenPublicUrl(getIndeedApplicationLink()) || "--"}
                      </p>
                      {renderPublicLinkActions(
                        "indeedApplication",
                        getIndeedApplicationLink(),
                      )}
                    </div>

                    {showTechnicalBoardConfig ? (
                      <div className="ai-public-links-channel">
                        <span className="ai-public-links-kicker">
                          Indeed integration
                        </span>
                        <h4 className="ai-public-links-channel-title">
                          Webhook URL
                          <span
                            className="ai-public-links-info"
                            title="After adding this webhook, Indeed will automatically send applications to Albert AI"
                          >
                            i
                          </span>
                        </h4>
                        <p className="ai-public-links-channel-desc">
                          Add in Indeed Employer → Job Applications → Webhook
                          Settings.
                        </p>
                        <p
                          className="ai-public-links-path ai-public-links-path-mono"
                          title={getWebhookUrl("indeed") || ""}
                        >
                          {shortenPublicUrl(getWebhookUrl("indeed")) || "--"}
                        </p>
                        {renderPublicLinkActions(
                          "indeedWebhook",
                          getWebhookUrl("indeed"),
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            {showTechnicalBoardConfig ? (
              <div className="ai-card">
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Agency Settings</p>
                  <h3 className="ai-card-title">Job Board Connections</h3>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: connectedProviders.length ? "#047857" : "#6b7280",
                  }}
                >
                  {boardSettingsLoading
                    ? "Loading..."
                    : connectedProviders.length
                    ? "✅ Connected"
                    : "Not connected"}
                </div>
              </div>
              {boardSettingsError && (
                <div className="ai-alert ai-alert-error">{boardSettingsError}</div>
              )}
              <div className="ai-settings-fields">
                <p className="ai-help">
                  Job board credentials are configured by your Albert admin.
                </p>
                {BOARD_PROVIDERS.map((provider) => {
                  const row = providerSettings?.[provider] || {};
                  const feedUrl = getFeedUrl(provider);
                  const webhookUrl = getWebhookUrl(provider);
                  return (
                    <div
                      key={`provider-card-${provider}`}
                      style={{
                        border: "1px solid var(--ai-border)",
                        borderRadius: 14,
                        padding: 14,
                        background: "#f8fafc",
                      }}
                    >
                      <div className="ai-card-note">
                        {PROVIDER_LABELS[provider] || provider}
                      </div>
                      <p className="ai-help" style={{ marginTop: 4 }}>
                        {row?.isActive ? "✅ Connected" : "Not connected"} (configured by admin)
                      </p>
                      {row?.publisherId ? (
                        <p className="ai-help">Publisher ID: {row.publisherId}</p>
                      ) : null}
                      <div
                        style={{
                          fontWeight: 700,
                          color: "var(--ai-text)",
                          marginTop: 6,
                          wordBreak: "break-all",
                        }}
                      >
                        Feed: {feedUrl || "--"}
                      </div>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "var(--ai-text)",
                          marginTop: 6,
                          wordBreak: "break-all",
                        }}
                      >
                        Webhook: {webhookUrl || "--"}
                      </div>
                      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          className="ai-button ai-button-sm ai-button-secondary"
                          onClick={() => copyText(`feed-${provider}`, feedUrl)}
                          disabled={!feedUrl}
                        >
                          {copiedLinkKey === `feed-${provider}` ? "Copied Feed" : "Copy Feed"}
                        </button>
                        <button
                          type="button"
                          className="ai-button ai-button-sm ai-button-secondary"
                          onClick={() => copyText(`webhook-${provider}`, webhookUrl)}
                          disabled={!webhookUrl}
                        >
                          {copiedLinkKey === `webhook-${provider}`
                            ? "Copied Webhook"
                            : "Copy Webhook"}
                        </button>
                      </div>
                    </div>
                  );
                })}
                <p className="ai-help">
                  Need changes? Contact your Albert admin to update board
                  credentials.
                </p>
              </div>
              </div>
            ) : null}

            <div className="ai-actions ai-actions-sticky">
              <button
                type="button"
                className="ai-button ai-button-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>

            {jobModalOpen && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h2>{jobEditingId ? "Edit Job" : "Create Job"}</h2>
                    <button
                      className="modal-close-button"
                      onClick={() => {
                        setJobModalOpen(false);
                        resetJobForm();
                      }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="ai-settings-fields">
                      <div className="ai-form-group">
                        <label className="ai-label">Title</label>
                        <input
                          className="ai-input"
                          value={jobForm.title}
                          onChange={(e) =>
                            setJobForm((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                          placeholder="Caregiver – Night Shift"
                        />
                      </div>
                      <div className="ai-form-group">
                        <label className="ai-label">Description</label>
                        <textarea
                          className="ai-textarea"
                          value={jobForm.description}
                          onChange={(e) =>
                            setJobForm((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                          rows="3"
                        />
                      </div>
                      <div className="ai-form-group">
                        <label className="ai-label">Requirements</label>
                        <textarea
                          className="ai-textarea"
                          value={jobForm.requirements}
                          onChange={(e) =>
                            setJobForm((prev) => ({
                              ...prev,
                              requirements: e.target.value,
                            }))
                          }
                          rows="3"
                        />
                      </div>
                      <div className="ai-form-group">
                        <label className="ai-label">Requirement File (optional)</label>
                        <input
                          className="ai-input"
                          type="file"
                          accept=".pdf,.doc,.docx,.txt,.rtf"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setJobForm((prev) => ({
                              ...prev,
                              requirementFile: file,
                              requirementFileName: file?.name || prev.requirementFileName || "",
                              requirementFileType: file?.type || prev.requirementFileType || "",
                            }));
                          }}
                        />
                        <p className="ai-help">
                          {jobForm.requirementFile
                            ? `Selected: ${jobForm.requirementFile.name}`
                            : jobForm.requirementFileName
                              ? `Current file: ${jobForm.requirementFileName}`
                              : "No file selected."}{" "}
                          This file is stored for your team&apos;s reference only. You
                          still need to enter Description and Requirements above —
                          the system does not read or extract text from the uploaded
                          file for job postings or AI screening.
                        </p>
                      </div>
                      <div className="ai-form-group">
                        <label className="ai-label">Requirement Tags</label>
                        <div className="ai-tag-chip-row">
                          {DEFAULT_REQUIREMENT_TAGS.map((tag) => {
                            const selected = (jobForm.requirementTags || []).includes(tag);
                            return (
                              <button
                                key={tag}
                                type="button"
                                className={`ai-tag-chip ${selected ? "selected" : ""}`}
                                onClick={() => toggleRequirementTag(tag)}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                        <div className="ai-tag-custom-row">
                          <input
                            className="ai-input"
                            value={customRequirementTag}
                            onChange={(e) => setCustomRequirementTag(e.target.value)}
                            placeholder="Add custom tag (example: nightshift)"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addCustomRequirementTag();
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="ai-button ai-button-sm ai-button-secondary"
                            onClick={addCustomRequirementTag}
                          >
                            Add Tag
                          </button>
                        </div>
                        {(jobForm.requirementTags || []).length ? (
                          <div className="ai-tag-chip-row" style={{ marginTop: 8 }}>
                            {(jobForm.requirementTags || []).map((tag) => (
                              <button
                                key={`selected-${tag}`}
                                type="button"
                                className="ai-tag-chip selected"
                                onClick={() => toggleRequirementTag(tag)}
                                title="Click to remove"
                              >
                                {tag} ×
                              </button>
                            ))}
                          </div>
                        ) : null}
                        <p className="ai-help">
                          Tags are used by AI matching and scoring (for example,{" "}
                          <strong>nightshift</strong>).
                        </p>
                      </div>
                      {evaluationCriteriaEnabled ? (
                        <div className="ai-form-group">
                          <label className="ai-label">Our requirements for this role</label>
                          <p className="ai-help" style={{ marginTop: 0 }}>
                            Every candidate for this job is checked against this requirements
                            list. Uncheck anything that doesn&apos;t apply for this specific job.
                          </p>
                          {Object.keys(EVALUATION_CRITERIA_CATEGORY_LABELS).map((category) => {
                            const itemsInCategory = (jobForm.evaluationCriteria || []).filter(
                              (item) => item.category === category,
                            );
                            if (!itemsInCategory.length) return null;
                            return (
                              <div key={category} style={{ marginBottom: 12 }}>
                                <p
                                  className="ai-help"
                                  style={{ marginBottom: 4, fontWeight: 600 }}
                                >
                                  {EVALUATION_CRITERIA_CATEGORY_LABELS[category]}
                                </p>
                                {itemsInCategory.map((item) => (
                                  <label
                                    key={item.criteriaId}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      marginBottom: 6,
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={item.isActive !== false}
                                      onChange={() => toggleEvaluationCriteria(item.criteriaId)}
                                    />
                                    <span>{item.label}</span>
                                    {item.isCustom ? (
                                      <button
                                        type="button"
                                        className="ai-button ai-button-sm ai-button-secondary"
                                        onClick={() => removeEvaluationCriteria(item.criteriaId)}
                                      >
                                        Remove
                                      </button>
                                    ) : null}
                                  </label>
                                ))}
                              </div>
                            );
                          })}
                          <div className="ai-tag-custom-row">
                            <input
                              className="ai-input"
                              value={customCriteriaLabel}
                              onChange={(e) => setCustomCriteriaLabel(e.target.value)}
                              placeholder="Add custom item (example: bilingual Spanish)"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addCustomEvaluationCriteria();
                                }
                              }}
                            />
                            <select
                              className="ai-select"
                              value={customCriteriaCategory}
                              onChange={(e) => setCustomCriteriaCategory(e.target.value)}
                            >
                              {Object.keys(EVALUATION_CRITERIA_CATEGORY_LABELS).map((category) => (
                                <option key={category} value={category}>
                                  {EVALUATION_CRITERIA_CATEGORY_LABELS[category]}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="ai-button ai-button-sm ai-button-secondary"
                              onClick={addCustomEvaluationCriteria}
                            >
                              + Add custom item
                            </button>
                          </div>
                        </div>
                      ) : null}
                      <div className="ai-inline-grid">
                        <div className="ai-form-group">
                          <label className="ai-label">Location</label>
                          <input
                            className="ai-input"
                            value={jobForm.location}
                            onChange={(e) =>
                              setJobForm((prev) => ({
                                ...prev,
                                location: e.target.value,
                              }))
                            }
                            placeholder="Dallas, TX"
                          />
                        </div>
                        <div className="ai-form-group">
                          <label className="ai-label">Zip Code (required)</label>
                          <input
                            className="ai-input"
                            value={jobForm.zipCode}
                            onChange={(e) =>
                              setJobForm((prev) => ({
                                ...prev,
                                zipCode: e.target.value,
                              }))
                            }
                            placeholder="20166"
                            inputMode="numeric"
                            maxLength={10}
                            autoComplete="postal-code"
                          />
                          <p className="ai-help">One US zip code only</p>
                        </div>
                        <div className="ai-form-group">
                          <label className="ai-label">Min years</label>
                          <input
                            className="ai-input"
                            type="number"
                            value={jobForm.minYears}
                            onChange={(e) =>
                              setJobForm((prev) => ({
                                ...prev,
                                minYears: e.target.value,
                              }))
                            }
                            placeholder="3"
                            min="0"
                          />
                        </div>
                        <div className="ai-form-group">
                          <label className="ai-label">Posted Date</label>
                          <input
                            className="ai-input"
                            type="date"
                            value={jobForm.postedDate}
                            onChange={(e) =>
                              setJobForm((prev) => ({
                                ...prev,
                                postedDate: e.target.value,
                              }))
                            }
                          />
                          <p className="ai-help">MM/DD/YYYY</p>
                        </div>
                        <div className="ai-form-group">
                          <label className="ai-label">Application Deadline</label>
                          <input
                            className="ai-input"
                            type="date"
                            value={jobForm.expiryDate}
                            min={jobForm.postedDate || undefined}
                            onChange={(e) =>
                              setJobForm((prev) => ({
                                ...prev,
                                expiryDate: e.target.value,
                              }))
                            }
                          />
                        <p className="ai-help">
                          Optional. Last day candidates can submit an application.
                          After this date, the job listing closes and the apply link
                          stops accepting new applications.
                        </p>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <p className="ai-help" style={{ margin: 0 }}>
                            MM/DD/YYYY
                          </p>
                            <button
                              type="button"
                              className="ai-button ai-button-sm ai-button-secondary"
                              onClick={() =>
                                setJobForm((prev) => ({ ...prev, expiryDate: "" }))
                              }
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                        <div className="ai-form-group">
                          <label className="ai-label">Salary</label>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 12,
                              marginBottom: 10,
                            }}
                          >
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <input
                                type="radio"
                                name="salary-visibility"
                                checked={
                                  jobForm.salaryType === "hourly" &&
                                  jobForm.hourlyPayStyle === "single"
                                }
                                onChange={() =>
                                  setJobForm((prev) => ({
                                    ...prev,
                                    salaryType: "hourly",
                                    hourlyPayStyle: "single",
                                  }))
                                }
                              />
                              <span>Per Hour</span>
                            </label>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <input
                                type="radio"
                                name="salary-visibility"
                                checked={
                                  jobForm.salaryType === "hourly" &&
                                  jobForm.hourlyPayStyle === "range"
                                }
                                onChange={() =>
                                  setJobForm((prev) => ({
                                    ...prev,
                                    salaryType: "hourly",
                                    hourlyPayStyle: "range",
                                  }))
                                }
                              />
                              <span>Per Hour (range)</span>
                            </label>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <input
                                type="radio"
                                name="salary-visibility"
                                checked={
                                  jobForm.salaryType === "annually" &&
                                  jobForm.hourlyPayStyle === "single"
                                }
                                onChange={() =>
                                  setJobForm((prev) => ({
                                    ...prev,
                                    salaryType: "annually",
                                    hourlyPayStyle: "single",
                                  }))
                                }
                              />
                              <span>Annually</span>
                            </label>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <input
                                type="radio"
                                name="salary-visibility"
                                checked={
                                  jobForm.salaryType === "annually" &&
                                  jobForm.hourlyPayStyle === "range"
                                }
                                onChange={() =>
                                  setJobForm((prev) => ({
                                    ...prev,
                                    salaryType: "annually",
                                    hourlyPayStyle: "range",
                                  }))
                                }
                              />
                              <span>Annually (range)</span>
                            </label>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <input
                                type="radio"
                                name="salary-visibility"
                                checked={jobForm.salaryType === "competitive"}
                                onChange={() =>
                                  setJobForm((prev) => ({
                                    ...prev,
                                    salaryType: "competitive",
                                    hourlyPayStyle: "single",
                                    salary: "",
                                    salaryMin: "",
                                    salaryMax: "",
                                  }))
                                }
                              />
                              <span>Competitive Salary</span>
                            </label>
                          </div>
                          <div>
                          {jobForm.salaryType === "competitive" ? (
                            <p className="ai-help">This job will show: Competitive Salary</p>
                          ) : jobForm.salaryType === "hourly" &&
                            jobForm.hourlyPayStyle === "range" ? (
                            <>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: 10,
                                }}
                              >
                                <div>
                                  <label className="ai-label" style={{ fontSize: 12 }}>
                                    Min ($/hr)
                                  </label>
                                  <input
                                    className="ai-input"
                                    inputMode="numeric"
                                    value={jobForm.salaryMin}
                                    onChange={(e) =>
                                      setJobForm((prev) => ({
                                        ...prev,
                                        salaryMin: sanitizePayInput(e.target.value),
                                      }))
                                    }
                                    placeholder="18.50"
                                  />
                                </div>
                                <div>
                                  <label className="ai-label" style={{ fontSize: 12 }}>
                                    Max ($/hr)
                                  </label>
                                  <input
                                    className="ai-input"
                                    inputMode="numeric"
                                    value={jobForm.salaryMax}
                                    onChange={(e) =>
                                      setJobForm((prev) => ({
                                        ...prev,
                                        salaryMax: sanitizePayInput(e.target.value),
                                      }))
                                    }
                                    placeholder="22.75"
                                  />
                                </div>
                              </div>
                              <p className="ai-help" style={{ marginTop: 8 }}>
                                {(() => {
                                  const min = parsePayValue(jobForm.salaryMin);
                                  const max = parsePayValue(jobForm.salaryMax);
                                  if (!min || !max) return "Preview: --";
                                  return `Preview: $${min.toLocaleString("en-US")}-$${max.toLocaleString("en-US")} per hour`;
                                })()}
                              </p>
                            </>
                          ) : jobForm.salaryType === "hourly" ? (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  border: "1px solid var(--ai-border)",
                                  borderRadius: 10,
                                  padding: "8px 10px",
                                  background: "#fff",
                                }}
                              >
                                <span style={{ fontWeight: 700, color: "var(--ai-text)" }}>$</span>
                                <input
                                  className="ai-input"
                                  style={{ marginBottom: 0 }}
                                  inputMode="numeric"
                                  value={jobForm.salary}
                                  onChange={(e) =>
                                    setJobForm((prev) => ({
                                      ...prev,
                                      salary: sanitizePayInput(e.target.value),
                                    }))
                                  }
                                  placeholder="20.00"
                                />
                                <span style={{ color: "var(--ai-muted)", fontSize: 13 }}>
                                  per hour
                                </span>
                              </div>
                              <p className="ai-help" style={{ marginTop: 8 }}>
                                {(() => {
                                  const raw = parsePayValue(jobForm.salary);
                                  if (!raw) return "Preview: --";
                                  return `Preview: $${raw.toLocaleString("en-US")} per hour`;
                                })()}
                              </p>
                            </>
                          ) : jobForm.salaryType === "annually" &&
                            jobForm.hourlyPayStyle === "range" ? (
                            <>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: 10,
                                }}
                              >
                                <div>
                                  <label className="ai-label" style={{ fontSize: 12 }}>
                                    Min ($/yr)
                                  </label>
                                  <input
                                    className="ai-input"
                                    inputMode="numeric"
                                    value={jobForm.salaryMin}
                                    onChange={(e) =>
                                      setJobForm((prev) => ({
                                        ...prev,
                                        salaryMin: sanitizePayInput(e.target.value),
                                      }))
                                    }
                                    placeholder="45000"
                                  />
                                </div>
                                <div>
                                  <label className="ai-label" style={{ fontSize: 12 }}>
                                    Max ($/yr)
                                  </label>
                                  <input
                                    className="ai-input"
                                    inputMode="numeric"
                                    value={jobForm.salaryMax}
                                    onChange={(e) =>
                                      setJobForm((prev) => ({
                                        ...prev,
                                        salaryMax: sanitizePayInput(e.target.value),
                                      }))
                                    }
                                    placeholder="55000"
                                  />
                                </div>
                              </div>
                              <p className="ai-help" style={{ marginTop: 8 }}>
                                {(() => {
                                  const min = parsePayValue(jobForm.salaryMin);
                                  const max = parsePayValue(jobForm.salaryMax);
                                  if (!min || !max) return "Preview: --";
                                  return `Preview: $${min.toLocaleString("en-US")}-$${max.toLocaleString("en-US")} annually`;
                                })()}
                              </p>
                            </>
                          ) : jobForm.salaryType === "annually" ? (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  border: "1px solid var(--ai-border)",
                                  borderRadius: 10,
                                  padding: "8px 10px",
                                  background: "#fff",
                                }}
                              >
                                <span style={{ fontWeight: 700, color: "var(--ai-text)" }}>$</span>
                                <input
                                  className="ai-input"
                                  style={{ marginBottom: 0 }}
                                  inputMode="numeric"
                                  value={jobForm.salary}
                                  onChange={(e) =>
                                    setJobForm((prev) => ({
                                      ...prev,
                                      salary: sanitizePayInput(e.target.value),
                                    }))
                                  }
                                  placeholder="45000"
                                />
                                <span style={{ color: "var(--ai-muted)", fontSize: 13 }}>
                                  annually
                                </span>
                              </div>
                              <p className="ai-help" style={{ marginTop: 8 }}>
                                {(() => {
                                  const raw = parsePayValue(jobForm.salary);
                                  if (!raw) return "Preview: --";
                                  return `Preview: $${raw.toLocaleString("en-US")} annually`;
                                })()}
                              </p>
                            </>
                          ) : null}
                        </div>
                        </div>
                      </div>

                      <div className="ai-form-group">
                        <label className="ai-label">Custom Questions</label>
                        <div className="ai-settings-fields">
                          {(jobForm.questions || [""]).map((q, idx) => (
                            <div
                              key={`${idx}`}
                              style={{ display: "flex", gap: 10 }}
                            >
                              <input
                                className="ai-input"
                                value={q}
                                onChange={(e) =>
                                  setJobForm((prev) => {
                                    const next = Array.isArray(prev.questions)
                                      ? [...prev.questions]
                                      : [];
                                    next[idx] = e.target.value;
                                    return { ...prev, questions: next };
                                  })
                                }
                                placeholder="Enter a job-specific question..."
                              />
                              <button
                                type="button"
                                className="ai-button ai-button-sm ai-button-secondary"
                                onClick={() =>
                                  setJobForm((prev) => {
                                    const next = Array.isArray(prev.questions)
                                      ? [...prev.questions]
                                      : [];
                                    next.splice(idx, 1);
                                    return {
                                      ...prev,
                                      questions: next.length ? next : [""],
                                    };
                                  })
                                }
                                disabled={(jobForm.questions || []).length <= 1}
                                title="Remove"
                              >
                                Remove
                              </button>
                            </div>
                          ))}

                          <div>
                            <button
                              type="button"
                              className="ai-button ai-button-secondary"
                              disabled={
                                (jobForm.questions || []).length >=
                                MAX_CUSTOM_QUESTIONS
                              }
                              title={
                                (jobForm.questions || []).length >=
                                MAX_CUSTOM_QUESTIONS
                                  ? `Maximum of ${MAX_CUSTOM_QUESTIONS} custom questions`
                                  : undefined
                              }
                              onClick={() =>
                                setJobForm((prev) => {
                                  const current = Array.isArray(prev.questions)
                                    ? prev.questions
                                    : [];
                                  if (current.length >= MAX_CUSTOM_QUESTIONS) {
                                    return prev;
                                  }
                                  return {
                                    ...prev,
                                    questions: [...current, ""],
                                  };
                                })
                              }
                            >
                              Add Question
                            </button>
                          </div>
                        </div>
                        <p className="ai-help">
                          {(jobForm.questions || []).length}/
                          {MAX_CUSTOM_QUESTIONS} questions used.{" "}
                          Video interviews: these questions are guaranteed to be asked, in addition to the standard interview questions. Phone interviews: they're sent along with the call, but only take effect if your ElevenLabs interview agent's prompt has been configured to use them — talk to support if you need that set up.
                        </p>
                      </div>

                      <div className="ai-form-group">
                        <label className="ai-label">AI Interview Focus</label>
                        <div
                          className={`ai-video-toggle-shell ${
                            jobForm.phoneInterviewFocusEnabled
                              ? "ai-video-toggle-shell-enabled"
                              : "ai-video-toggle-shell-disabled"
                          }`}
                        >
                          <div className="ai-video-toggle-copy">
                            <p className="ai-video-toggle-title">
                              🎯 Guide the AI interviewer
                            </p>
                            <p className="ai-video-toggle-subtitle">
                              When enabled, this job's phone interview uses
                              your agency's "Phone Interview Focus" guidance
                              (set on the main settings page). When disabled,
                              the AI interviewer skips that guidance for this
                              job only.
                            </p>
                          </div>
                          <button
                            type="button"
                            aria-label={
                              jobForm.phoneInterviewFocusEnabled
                                ? "Disable AI interview focus guidance for this job"
                                : "Enable AI interview focus guidance for this job"
                            }
                            className={`ai-switch ${
                              jobForm.phoneInterviewFocusEnabled ? "on" : "off"
                            }`}
                            onClick={() =>
                              setJobForm((prev) => ({
                                ...prev,
                                phoneInterviewFocusEnabled:
                                  !prev.phoneInterviewFocusEnabled,
                              }))
                            }
                          >
                            <span className="ai-switch-knob" />
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>

                  <div className="ai-actions" style={{ marginTop: 16 }}>
                    <button
                      type="button"
                      className="ai-button ai-button-secondary"
                      onClick={() => {
                        setJobModalOpen(false);
                        resetJobForm();
                      }}
                      disabled={jobSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ai-button ai-button-primary"
                      onClick={handleSaveJob}
                      disabled={jobSaving}
                    >
                      {jobSaving ? "Saving..." : "Save and Post Job"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AiHiringSettings;
